import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Modal } from '../../../shared/Modal';
import { Button } from '../../../shared/Button';
import { Accordion } from '../../../shared/Accordion';
import { useToast } from '../../../shared/Toast';
import { useAccount } from '../../../../hooks/useAccount';
import { useSchedulingOptions } from '../../../../hooks/useSchedulingOptions';
import axiosInstance from '../../../../services/api';

// V2 Components (reused)
import { SchedulingAccordion } from '../../SchedulingAccordion';
import VideoTemplatePreview from '../../VideoTemplatePreview';
import TemplateVariableEditor from '../../TemplateVariableEditor';
import { VisualEffectsPanel } from '../../VisualEffectsPanel';
import { GlobalAISuggestionBanner } from '../../GlobalAISuggestionBanner';
import { RemotionPreview } from '../../RemotionPreview';
import { BackgroundSelector } from '../../BackgroundSelector';
import { TextAnimationSelector } from '../../TextAnimationSelector';
import { ProductFontSelector } from '../../ProductFontSelector';

// V3 Hooks and Sections
import { useProductApprovalState } from '../hooks/useProductApprovalState';
import { ModalActionFooter } from '../sections/ModalActionFooter';
import { CAPTION_COLOR_PRESETS } from '../constants/defaults';

/**
 * ProductInfoSummary Component
 *
 * Displays product information in a card
 */
function ProductInfoSummary({ productInfo, showPriceDisclaimer, onTogglePriceDisclaimer, priceDisclaimerLabel, disabled }) {
  const { productTitle, salePrice, originalPrice, discountPercent } = productInfo;

  return (
    <div className="bg-purple-50 rounded-lg p-4 space-y-2">
      <h4 className="font-semibold text-purple-900">{productTitle}</h4>
      <div className="flex items-baseline gap-2">
        {salePrice && (
          <span className="text-xl font-bold text-purple-600">{salePrice}</span>
        )}
        {originalPrice && (
          <span className="text-sm text-gray-500 line-through">{originalPrice}</span>
        )}
        {discountPercent && (
          <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
            -{discountPercent}%
          </span>
        )}
      </div>
      {showPriceDisclaimer !== undefined && (
        <div className="flex items-center justify-between pt-2 border-t border-purple-200 mt-2">
          <div className="flex-1">
            <span className="text-xs font-medium text-purple-800">
              {priceDisclaimerLabel}
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showPriceDisclaimer}
            onClick={onTogglePriceDisclaimer}
            disabled={disabled}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              showPriceDisclaimer ? 'bg-purple-600' : 'bg-gray-200'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                showPriceDisclaimer ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * RejectAccordion Component
 *
 * Accordion for rejecting product proposals
 */
function RejectAccordion({
  reason,
  onReasonChange,
  onReject,
  isRejecting,
  disabled,
  t,
}) {
  return (
    <Accordion
      title={t('modal.product.rejectTitle', { defaultValue: 'Reject Proposal' })}
      icon="⚠️"
      defaultExpanded={false}
      variant="danger"
    >
      <div className="space-y-4 pt-4">
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <svg
            className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-red-800">
            {t('modal.product.rejectWarning', {
              defaultValue: 'This will reject the product proposal. The product will not be posted.',
            })}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('modal.product.rejectReasonLabel', { defaultValue: 'Reason for rejection' })}
          </label>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={t('modal.product.rejectReasonPlaceholder', {
              defaultValue: 'Please provide a reason...',
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
            rows={3}
            disabled={isRejecting}
          />
        </div>

        <Button
          variant="danger"
          onClick={onReject}
          loading={isRejecting}
          disabled={isRejecting || disabled}
          className="w-full"
        >
          {isRejecting
            ? t('modal.product.rejecting', { defaultValue: 'Rejecting...' })
            : t('modal.product.rejectButton', { defaultValue: 'Reject Proposal' })}
        </Button>
      </div>
    </Accordion>
  );
}

/**
 * ProductApprovalModal Component
 *
 * Modal for approving e-commerce product proposals.
 *
 * Features:
 * - Live Remotion video preview
 * - Template variable editing
 * - Caption and color customization
 * - Visual effects configuration
 * - Background and animation selection
 * - AI suggestion application
 * - Scheduling options
 */
export function ProductApprovalModal({
  item,
  isOpen,
  onClose,
  onApproveProduct,
  onRejectProduct,
  onUpdate,
}) {
  const { t } = useTranslation('approval');
  const toast = useToast();
  const { selectedAccountId } = useAccount();

  // Scheduling options
  const scheduling = useSchedulingOptions(selectedAccountId, isOpen);

  // Product state from custom hook
  const productState = useProductApprovalState(item);

  // Load brand language for CTA texts
  const [brandLanguage, setBrandLanguage] = useState('de');
  useEffect(() => {
    if (!selectedAccountId || !isOpen) return;
    axiosInstance.get(`/accounts/${selectedAccountId}/brand-settings`)
      .then(res => {
        const lang = res?.data?.language;
        if (lang) setBrandLanguage(lang);
      })
      .catch(() => { /* keep default */ });
  }, [selectedAccountId, isOpen]);

  // UI State
  const [isApprovingProduct, setIsApprovingProduct] = useState(false);
  const [isRejectingProduct, setIsRejectingProduct] = useState(false);
  const [isRegeneratingCaption, setIsRegeneratingCaption] = useState(false);

  /**
   * Handle product approval
   */
  const handleApproveProductClick = useCallback(async () => {
    setIsApprovingProduct(true);
    try {
      await onApproveProduct?.(item, {
        caption_text: productState.productCaption,
        caption_color: productState.captionColor,
        caption_font: productState.captionFont,
        scheduled_for: scheduling.scheduledTime,
        variables_override: productState.templateVariables,
        effects: productState.visualEffects,
        background_type: productState.backgroundType,
        background_palette: productState.backgroundPalette,
        text_animation: productState.textAnimation,
        show_price_disclaimer: productState.showPriceDisclaimer,
      });
      toast.success(t('modal.product.toast.approved', { defaultValue: 'Product approved!' }));
      onClose();
    } catch (error) {
      console.error('Product approval error:', error);
      toast.error(
        t('modal.product.toast.approveError', {
          defaultValue: 'Failed to approve product',
          error: error.message,
        })
      );
    } finally {
      setIsApprovingProduct(false);
    }
  }, [item, productState, scheduling, onApproveProduct, onClose, toast, t]);

  /**
   * Handle caption regeneration
   */
  const handleRegenerateCaption = useCallback(async () => {
    setIsRegeneratingCaption(true);
    try {
      const response = await axiosInstance.post(
        `/ecommerce/proposals/${item.proposal_id}/regenerate-caption`,
        {},
        { params: { instagram_account_id: selectedAccountId } }
      );
      if (response.data?.caption_text) {
        productState.setProductCaption(response.data.caption_text);
        toast.success(
          t('modal.product.toast.captionRegenerated', { defaultValue: 'Caption regenerated!' })
        );
      }
    } catch (error) {
      console.error('Caption regeneration error:', error);
      toast.error(
        t('modal.product.toast.captionRegenerateError', {
          defaultValue: 'Failed to regenerate caption',
          error: error.message,
        })
      );
    } finally {
      setIsRegeneratingCaption(false);
    }
  }, [item, selectedAccountId, productState, toast, t]);

  /**
   * Handle product rejection
   */
  const handleRejectProductClick = useCallback(async () => {
    setIsRejectingProduct(true);
    try {
      await onRejectProduct?.(item, productState.productRejectReason);
      toast.success(t('modal.product.toast.rejected', { defaultValue: 'Product rejected' }));
      onClose();
    } catch (error) {
      console.error('Product rejection error:', error);
      toast.error(
        t('modal.product.toast.rejectError', {
          defaultValue: 'Failed to reject product',
          error: error.message,
        })
      );
    } finally {
      setIsRejectingProduct(false);
    }
  }, [item, productState.productRejectReason, onRejectProduct, onClose, toast, t]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!item) return null;

  const isProcessing = isApprovingProduct || isRejectingProduct || isRegeneratingCaption;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      title={t('modal.product.title', { defaultValue: 'Product Proposal' })}
      closeOnBackdrop={false}
      closeOnEscape={!isProcessing}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 -mx-6 -my-4 lg:h-[calc(100vh-160px)]">
        {/* Left Column - Live Remotion Preview */}
        <div className="lg:sticky lg:top-0 lg:self-start bg-gray-50 p-6 lg:h-[calc(100vh-160px)] flex items-center justify-center">
          <div className="w-full max-w-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
              {t('modal.preview.title', { defaultValue: 'Preview' })}
            </h3>
            {productState.isRendering ? (
              <VideoTemplatePreview
                previewUrl={productState.previewUrl}
                templateId={item.template_id}
                variables={productState.templateVariables}
                isRendering={productState.isRendering}
              />
            ) : item.template_variables?.video_url ? (
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <video
                  src={item.template_variables.video_url}
                  className="w-full h-auto"
                  style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 280px)' }}
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
            ) : (
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <RemotionPreview
                  templateId={item.template_id}
                  templateVariables={{
                    ...productState.templateVariables,
                    headline_text: productState.templateVariables?.headline_text ||
                      item?.llm_suggestion?.style?.headlineText ||
                      'Für dich entdeckt',
                  }}
                  captionText={productState.productCaption}
                  captionColor={productState.captionColor}
                  captionFont={productState.captionFont}
                  effects={productState.visualEffects}
                  backgroundType={productState.backgroundType}
                  backgroundPalette={productState.backgroundPalette}
                  textAnimation={productState.textAnimation}
                  language={brandLanguage}
                  showPriceDisclaimer={productState.showPriceDisclaimer}
                  controls={true}
                  autoPlay={false}
                  loop={true}
                  className="w-full h-auto"
                  style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 280px)' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Content & Controls */}
        <div className="flex flex-col lg:h-[calc(100vh-160px)]">
          {/* Scrollable Content */}
          <div className="flex-1 p-6 space-y-6 overflow-y-auto overscroll-contain">
            {/* Product Info Summary + PAngV Disclaimer */}
            <ProductInfoSummary
              productInfo={productState.productInfo}
              showPriceDisclaimer={item.template_id === 'existing_video' ? undefined : productState.showPriceDisclaimer}
              onTogglePriceDisclaimer={() => productState.setShowPriceDisclaimer(!productState.showPriceDisclaimer)}
              priceDisclaimerLabel={t('modal.product.priceDisclaimerLabel', { defaultValue: 'Preishinweis (PAngV)' })}
              disabled={isProcessing}
            />

            {/* Global AI Suggestion Banner */}
            {productState.aiSuggestion && (
              <GlobalAISuggestionBanner
                aiSuggestion={productState.aiSuggestion}
                onApply={productState.handleApplyAISuggestion}
                isApplied={productState.aiSuggestionApplied}
                disabled={productState.isRendering || isApprovingProduct}
              />
            )}

            {/* Template Settings Accordion */}
            <Accordion
              title={t('modal.product.templateSettingsTitle', { defaultValue: 'Template Settings' })}
              icon="⚙️"
              defaultExpanded={true}
              variant="neutral"
            >
              <div className="pt-4">
                <TemplateVariableEditor
                  templateId={productState.selectedTemplate}
                  variables={productState.templateVariables}
                  onChange={productState.handleTemplateVariablesChange}
                  disabled={productState.isRendering || isApprovingProduct}
                  product={item}
                />
              </div>
            </Accordion>

            {/* Caption & Text Accordion */}
            <Accordion
              title={t('modal.product.captionTextTitle', { defaultValue: 'Caption & Text' })}
              icon="✏️"
              defaultExpanded={false}
              variant="neutral"
            >
              <div className="pt-4 space-y-6">
                {/* Caption Text */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('modal.product.captionLabel', { defaultValue: 'Caption Text' })}
                  </label>
                  <textarea
                    value={productState.productCaption}
                    onChange={(e) => productState.setProductCaption(e.target.value)}
                    disabled={productState.isRendering || isApprovingProduct || isRegeneratingCaption}
                    placeholder={t('modal.product.captionPlaceholder', {
                      defaultValue: 'Enter caption text...',
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500">
                    {productState.productCaption.length} / 200{' '}
                    {t('modal.product.captionCounter', { defaultValue: 'characters' })}
                  </p>
                </div>

                {/* Caption Color Picker */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('modal.product.captionColorLabel', { defaultValue: 'Text Color' })}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={productState.captionColor}
                      onChange={(e) => productState.setCaptionColor(e.target.value)}
                      disabled={productState.isRendering || isApprovingProduct}
                      className="w-10 h-10 p-1 border border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
                    />
                    {/* Quick Presets */}
                    <div className="flex gap-2">
                      {CAPTION_COLOR_PRESETS.map(({ color, name }) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => productState.setCaptionColor(color)}
                          className={`w-8 h-8 rounded-full border-2 ${
                            productState.captionColor === color
                              ? 'border-purple-500'
                              : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                          title={name}
                          disabled={productState.isRendering || isApprovingProduct}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">
                      {productState.captionColor.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Caption Font Selector */}
                <div className="border-t border-gray-200 pt-4">
                  <ProductFontSelector
                    captionFont={productState.captionFont}
                    onCaptionFontChange={productState.setCaptionFont}
                    llmSuggested={item?.llm_suggestion?.style || null}
                    accountId={selectedAccountId}
                    compact={false}
                  />
                </div>

                {/* Regenerate Caption Button */}
                <div className="border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={handleRegenerateCaption}
                    disabled={productState.isRendering || isApprovingProduct || isRegeneratingCaption}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Sparkles size={16} />
                    {isRegeneratingCaption
                      ? t('modal.product.regeneratingCaption', { defaultValue: 'Regenerating...' })
                      : t('modal.product.regenerateCaption', { defaultValue: 'Regenerate Caption' })}
                  </button>
                </div>
              </div>
            </Accordion>

            {/* Visual Style Accordion */}
            <Accordion
              title={t('modal.product.visualStyleTitle', { defaultValue: 'Visual Style' })}
              icon="🎨"
              defaultExpanded={false}
              variant="neutral"
            >
              <div className="pt-4 space-y-6">
                {/* Text Animation */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {t('modal.product.appearanceEffects', { defaultValue: 'Appearance Effects' })}
                  </h4>
                  <TextAnimationSelector
                    textAnimation={productState.textAnimation}
                    onTextAnimationChange={productState.setTextAnimation}
                    llmSuggested={item?.llm_suggestion?.style || null}
                    compact={false}
                  />
                </div>

                {/* Background Selector */}
                <div className="border-t border-gray-200 pt-4">
                  <BackgroundSelector
                    backgroundType={productState.backgroundType}
                    backgroundPalette={productState.backgroundPalette}
                    onBackgroundTypeChange={productState.setBackgroundType}
                    onBackgroundPaletteChange={productState.setBackgroundPalette}
                    llmSuggested={item?.llm_suggestion?.style || null}
                    compact={false}
                  />
                </div>

                {/* Visual Effects Panel */}
                <div className="border-t border-gray-200 pt-4">
                  <VisualEffectsPanel
                    effects={productState.visualEffects}
                    onChange={productState.setVisualEffects}
                    llmSuggested={
                      item?.llm_suggestion?.effects
                        ? {
                            ...item.llm_suggestion.effects,
                            reasoning: item.llm_suggestion.reasoning,
                          }
                        : null
                    }
                    disabled={productState.isRendering || isApprovingProduct}
                  />
                </div>
              </div>
            </Accordion>

            {/* Scheduling Accordion */}
            <SchedulingAccordion
              autoScheduledTime={scheduling.autoScheduledTime}
              manualScheduledTime={scheduling.manualScheduledTime}
              scheduledTime={scheduling.scheduledTime}
              isManualMode={scheduling.isManualMode}
              cascadeInfo={scheduling.cascadeInfo}
              postIntervalHours={scheduling.postIntervalHours}
              isLoading={scheduling.isLoading}
              error={scheduling.error}
              onManualTimeChange={scheduling.setManualTime}
              onClearManualTime={scheduling.clearManualTime}
              disabled={isProcessing}
            />

            {/* Reject Accordion */}
            <RejectAccordion
              reason={productState.productRejectReason}
              onReasonChange={productState.setProductRejectReason}
              onReject={handleRejectProductClick}
              isRejecting={isRejectingProduct}
              disabled={isApprovingProduct}
              t={t}
            />
          </div>

          {/* Sticky Action Footer */}
          <ModalActionFooter
            hasChanges={false}
            isProcessing={isApprovingProduct}
            disabled={productState.isRendering || isRejectingProduct || isRegeneratingCaption}
            onCancel={handleClose}
            onPrimaryAction={handleApproveProductClick}
            primaryLabel={item?.status === 'video_ready'
              ? t('modal.product.approveScheduleButton', { defaultValue: 'Approve & Schedule' })
              : t('modal.product.approveButton', { defaultValue: 'Approve & Render' })}
            processingLabel={t('modal.product.approving', { defaultValue: 'Approving...' })}
          />
        </div>
      </div>
    </Modal>
  );
}

ProductApprovalModal.propTypes = {
  item: PropTypes.shape({
    proposal_id: PropTypes.string.isRequired,
    product_id: PropTypes.string,
    template_id: PropTypes.string,
    template_variables: PropTypes.object,
    caption_text: PropTypes.string,
    caption_color: PropTypes.string,
    status: PropTypes.string,
    preview_url: PropTypes.string,
    sas_url: PropTypes.string,
    llm_suggestion: PropTypes.object,
    effects: PropTypes.object,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApproveProduct: PropTypes.func,
  onRejectProduct: PropTypes.func,
  onUpdate: PropTypes.func,
};

export default ProductApprovalModal;
